import { useCallback, useEffect } from 'react';

import type { TypedDocumentNode } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
  GetWaiverRecordsListDocument,
  type GetWaiverRecordsListQuery,
  type GetWaiverRecordsListQueryVariables,
} from '@/gql/operations';

import type { WaiverGridRow } from '../data/types';

import {
  isLoadingWaiversAtom,
  loadedWaiversTokenAtom,
  reloadWaiversAtom,
  waiverRecordsAtom,
  waiversErrorAtom,
} from './waiverAtoms';

type WaiverRow = GetWaiverRecordsListQuery['clm_waiver_records'][number];

dayjs.extend(utc);

// Waiver letter dates are `timestamptz` values stored at UTC midnight (they
// represent a calendar date, not an instant). Read the UTC calendar date and
// build a local-midnight Date from it, so `toLocaleDateString()` renders the
// intended day regardless of the viewer's timezone (mirrors how engagement's
// local-midnight dates behave).
function parseLocalDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = dayjs.utc(value);
  return new Date(d.year(), d.month(), d.date());
}

// Typed document so client.query infers result/variable types without deprecated generics.
const WAIVER_RECORDS_QUERY = GetWaiverRecordsListDocument as TypedDocumentNode<
  GetWaiverRecordsListQuery,
  GetWaiverRecordsListQueryVariables
>;

// Fetch the full waiver dataset in pages instead of a single request. Loading the
// whole dataset at once can time out on the server, so we page through it in
// fixed-size batches (keyset pagination) and concatenate the results.
//
// Keyset pages are cheap and each page filters on the last-seen ordering key
// (`id`) via the index, so — unlike OFFSET — every page stays equally fast no
// matter how deep we page.
const PAGE_SIZE = 20000;

async function fetchAllWaiverPages(
  client: ReturnType<typeof useApolloClient>,
  isCancelled: () => boolean,
): Promise<WaiverRow[]> {
  const allRows: WaiverRow[] = [];
  let lastRow: WaiverRow | undefined;

  for (;;) {
    if (isCancelled()) break;

    const { data } = await client.query({
      query: WAIVER_RECORDS_QUERY,
      // Keyset cursor: page on id (the ordering key) so each page seeks via the
      // index instead of scanning past a growing offset.
      variables: {
        limit: PAGE_SIZE,
        where: lastRow ? { id: { _gt: lastRow.id } } : undefined,
      },
      fetchPolicy: 'no-cache',
    });

    const rows = data?.clm_waiver_records ?? [];
    allRows.push(...rows);

    // Last page reached when the server returns fewer rows than we asked for.
    if (rows.length < PAGE_SIZE) break;

    lastRow = rows.at(-1);
  }

  return allRows;
}

function mapWaiverRow(record: WaiverRow): WaiverGridRow {
  return {
    id: String(record.id),
    clientName: record.mlfsl_client?.ClientName1 ?? '',
    clientNumber: record.client_number ?? '',
    matterNumber: record.matter_number ?? '',
    adverseClientName: record.adverse_client_name ?? '',
    adverseContactSigner: record.adverse_contact_signer ?? '',
    adverseLetterDate: parseLocalDate(record.adverse_letter_date),
    adverseAttorney: record.adverse_mlb_attorney_on_letter_name ?? 'N/A',
    clientContact: record.client_contact ?? '',
    clientLetterDate: parseLocalDate(record.client_letter_date),
    clientAttorney: record.mlb_attorney_on_client_letter_name ?? 'N/A',
    multipleWaivers: record.multiple_waivers?.trim().toLowerCase() === 'yes' ? 'Yes' : 'No',
  };
}

/**
 * Loader hook — fetches the full waiver list once and stores the parsed rows in
 * shared Jotai atoms. Mount once at the Dashboard.
 *
 * The rows live in atoms that persist across navigation, so re-mounting the
 * dashboard (e.g. clicking back from the detail page) does not re-fetch. Only
 * loads on the first mount or when a reload is explicitly requested
 * (reloadToken changed). `enabled` defers the fetch until the waiver tab is
 * actually shown.
 */
export const useWaiverRecords = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const client = useApolloClient();
  const records = useAtomValue(waiverRecordsAtom);
  const setRecords = useSetAtom(waiverRecordsAtom);
  const setLoading = useSetAtom(isLoadingWaiversAtom);
  const setError = useSetAtom(waiversErrorAtom);
  const reloadToken = useAtomValue(reloadWaiversAtom);
  const [loadedToken, setLoadedToken] = useAtom(loadedWaiversTokenAtom);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    const alreadyLoaded = loadedToken === reloadToken && records.length > 0;

    const loadAll = async () => {
      setLoading(true);
      setError(null);

      try {
        // fetchPolicy 'no-cache' skips Apollo's normalized-cache write — the data
        // lives in shared Jotai atoms, so the Apollo cache isn't needed here.
        // Fetched in fixed-size keyset pages (see fetchAllWaiverPages) so a large
        // dataset doesn't time out on the server.
        const waiverRows = await fetchAllWaiverPages(client, () => cancelled);
        const rows = waiverRows.map(mapWaiverRow);

        if (!cancelled) {
          setRecords(rows);
          setLoadedToken(reloadToken);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to load waiver records'));
          setLoading(false);
        }
      }
    };

    if (!alreadyLoaded) {
      void loadAll();
    }

    return () => {
      cancelled = true;
    };
  }, [enabled, client, reloadToken, loadedToken, records.length, setRecords, setLoading, setError, setLoadedToken]);
};

/**
 * Reader hook — reads the loaded waiver rows from shared state. Filtering,
 * sorting and pagination are handled client-side by the grid.
 */
export const useWaiverRecordsValue = () => {
  const waiverRows = useAtomValue(waiverRecordsAtom);
  const isLoading = useAtomValue(isLoadingWaiversAtom);
  const error = useAtomValue(waiversErrorAtom);
  const setReloadToken = useSetAtom(reloadWaiversAtom);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), [setReloadToken]);

  return { waiverRows, isLoading, error, refetch };
};
